import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState } from '@/types';
import { adminAuthAPI } from '@/lib/auth';

interface AuthStore extends AuthState {
  /** True once checkAuth has run at least once — prevents nested AuthGuards
   *  from re-triggering it and causing a mount/unmount loop. */
  initialized: boolean;
  signIn: (email: string, password: string, mfaCode?: string) => Promise<{ requiresMfa?: boolean }>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      initialized: false,
      error: undefined,

      signIn: async (email: string, password: string, mfaCode?: string) => {
        set({ isLoading: true, error: undefined });
        
        try {
          const result = await adminAuthAPI.signIn(email, password, mfaCode);

          if (result.requiresMfa) {
            set({ isLoading: false });
            return { requiresMfa: true };
          }

          // The login response carries a minimal user (no roles/permissions);
          // hydrate the full profile so AuthGuard can gate on roles/permissions.
          const user = await adminAuthAPI.getProfile();

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            initialized: true,
            error: undefined,
          });

          return {};
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Sign in failed',
          });
          throw error;
        }
      },

      signOut: async () => {
        set({ isLoading: true });
        
        try {
          await adminAuthAPI.signOut();
        } catch (error) {
          console.error('Sign out error:', error);
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: undefined,
          });
        }
      },

      refreshToken: async () => {
        try {
          await adminAuthAPI.refreshToken();
          const user = await adminAuthAPI.getProfile();
          
          set({
            user,
            isAuthenticated: true,
            error: undefined,
          });
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            error: error instanceof Error ? error.message : 'Token refresh failed',
          });
          throw error;
        }
      },

      checkAuth: async () => {
        // Run at most once concurrently; nested guards rely on `initialized`.
        if (get().isLoading) return;

        const token = localStorage.getItem('admin_access_token');
        if (!token) {
          set({ user: null, isAuthenticated: false, isLoading: false, initialized: true });
          return;
        }

        set({ isLoading: true });
        try {
          const user = await adminAuthAPI.getProfile();
          set({ user, isAuthenticated: true, isLoading: false, initialized: true, error: undefined });
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            initialized: true,
            error: error instanceof Error ? error.message : 'Authentication check failed',
          });
        }
      },

      clearError: () => {
        set({ error: undefined });
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
